import { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import { makeAutoObservable, runInAction, toJS } from 'mobx'
import { matchPath } from 'react-router'
import type { WorkbookPage } from 'src/dto'
import { WorkbookPageColl } from 'src/dto'

import { TOAST_STR } from '@/constant'
import type { LocalStorageDataT } from '@/constant/localStorageData'
import { getMakeVariantCapabilities } from '@/constant/makeWorksheet/makeVariantSearch'
import {
  MakeLessonMaterialFilterService,
  type 공통지원교재필터,
  type 학습지필터,
} from '@/contentComponents/makeLessonMaterial/(filter)/_deprecated/service/MakeLessonMaterialFilter.service'
import { ANSWER_TYPE } from '@/contentComponents/makeLessonMaterial/(filter)/component/MakeLessonMaterialFilterItem'
import { _workbookController } from '@/controller/_workbook.controller'
import { workbookController } from '@/controller/workbook.controller'
import { worksheetController } from '@/controller/worksheet.controller'
import serviceRepo from '@/repository/service.repo'
import { RouteName } from '@/router'
import globalLoaderService from '@/service/globalLoader.service'
import type { StorageService } from '@/service/storage.service'
import { localStorageService, sessionStorageService } from '@/service/storage.service'
import { commonErrorMessage, toastError } from '@/utils/toast'

export const defaultWorksheetSupportedWorkbookFilterParams: 공통지원교재필터 = {
  pairX: null,
  similarX: 1,
  similarLevel: 'AS_IS',
  practiceTest: 'INCLUDE',
  onlyAutoScorable: true,
  excludePrevious: false,
  problemFilterType: 'ALL',
  excludeOOC: false,
  includeSameProblem: undefined,
}

type FilterRequestParams = {
  request?: Partial<
    ConstructorParameters<
      typeof MakeLessonMaterialFilterService<학습지필터.지원교재필터>
    >[0]['request']
  >
}

/**
 * @remarks 이 서비스는 2단계로 갈 때에는 삭제되지 않습니다. 2단계를 갔다가 3단계 완료시에는 삭제됩니다.
 * 특이하게 삭제되기 때문에, 삭제시점을 정확히 담은 static 함수 destroy를 만들었습니다.
 */
/**
 * @deprecated
 * 학습지 교재 공통으로 사용하였으나, 교재가 filter로 부터 분리 되면서 학습지에서만 사용하게 됨(_deprecated로 이동)
 * 추후 학습지에서도 필터를 걷어내게 되면, _deprecated 디렉토리 삭제 후 교재에서 쓰는 것 같이 쓰면 됨
 */
export class SupportedWorkbookDetailService {
  workbook: WorkbookEntity.SupportedWorkbook | undefined
  workbookPageColl?: WorkbookPageColl
  filter?: MakeLessonMaterialFilterService<학습지필터.지원교재필터>
  selectedPageId?: number
  isLoading = false
  showExampleProblemImage = !!localStorage.getItem('시중교재_예시문항보기_여부')
  constructor() {
    makeAutoObservable(this)
  }

  static storage = {
    saveDraft: _saveToStorage(sessionStorageService),
    save: _saveToStorage(localStorageService),
    get: _getItem(localStorageService),
    getDraft: _getItem(sessionStorageService),
    popDraft: (workbookId: WorkbookEntity.Workbook['id']) => {
      const draft = this.storage.getDraft(workbookId)
      this.storage.deleteDraft(workbookId)
      return draft
    },
    deleteDraft: (workbookId: WorkbookEntity.Workbook['id']) => {
      const sessionStorageMap = _getStorageMap(sessionStorageService)
      sessionStorageMap.delete(String(workbookId))
      _setStorageMap(sessionStorageService)(sessionStorageMap)
    },
    saveFromDraft: (workbookId: WorkbookEntity.Workbook['id']) => {
      const draft = this.storage.popDraft(workbookId)

      if (draft) {
        this.storage.save(workbookId, draft)
      }
    },
  }

  /**
   * @remarks 이 서비스는 특이하게 삭제되기 때문에 destory로직을 이 함수 안에 넣고 씁니다.
   */
  static destroy(
    params:
      | { reason: 'ON_NAVIGATE'; pathname: string }
      | {
          reason: 'ON_CREATE'
          sameOptionUse: boolean
        },
  ) {
    if (params.reason === 'ON_CREATE' && params.sameOptionUse) {
      return
    }

    if (params.reason === 'ON_NAVIGATE') {
      if (
        matchPath(params.pathname, [
          RouteName.editWorksheetProblems,
          RouteName.makeWorksheetByWorkbookDetail,
          RouteName._makeCustomWorkbookStep1ByWorkbookDetail,
        ])
      ) {
        return
      }
    }

    serviceRepo.del(SupportedWorkbookDetailService)
  }

  get selectedPage() {
    if (!this.selectedPageId) return

    return this.workbookPageColl?.pageMap.get(this.selectedPageId)
  }

  static getGroupedPages(workbookPageColl: WorkbookPageColl) {
    const groupedPages: WorkbookPage[][] = []
    let groupedPageItem: WorkbookPage[] = []
    if (!workbookPageColl || workbookPageColl.toArr.length === 0) {
      return
    }

    workbookPageColl.toArr.forEach((page, index) => {
      groupedPageItem.push(page)
      if (index < workbookPageColl.toArr.length - 1) {
        if (workbookPageColl.toArr[index].title !== workbookPageColl.toArr[index + 1].title) {
          groupedPages.push(groupedPageItem)
          groupedPageItem = []
        }
      }
      if (index === workbookPageColl.toArr.length - 1 && groupedPageItem.length > 0) {
        groupedPages.push(groupedPageItem)
      }
    })
    return groupedPages
  }

  static getWorkbookPageInfoByPresetProblemIds(
    _workbookPageColl: WorkbookPageColl,
    problemIds: number[],
  ) {
    // 주의) 성능 이슈로 한번 더 담음. 내부에서 임시로만 사용하기
    const _flattenPageSet = new Set()
    const workbookPageColl = new WorkbookPageColl(_workbookPageColl.toArr)
    let minWorkbookPage = workbookPageColl.toArr[0]

    problemIds.forEach((id) => {
      workbookPageColl.toArr.forEach((workbookPage) => {
        const pageDetail = workbookPage.pageProblemColl?.get(id)
        if (pageDetail) {
          workbookPage.pageProblemColl?.setCheck(id)
          _flattenPageSet.add(pageDetail.workbookPageId)
          if (workbookPage.page < minWorkbookPage.page) {
            minWorkbookPage = workbookPage
          }
        }
      })
    })

    return {
      selectedPageSize: _flattenPageSet.size,
      workbookPageColl,
      minWorkbookPage,
    }
  }

  fetchWorkbook = async (workbookId: WorkbookEntity.SupportedWorkbook['id']) => {
    try {
      const workbook = await _workbookController.fetchWorkbookDetailInfo(workbookId)
      return WorkbookEntity.createWorkbook<WorkbookEntity.SupportedWorkbookType>(
        workbook as unknown as WorkbookEntity.SupportedWorkbook,
      )
    } catch (e) {
      commonErrorMessage(e)
      throw e
    }
  }

  initialize = async ({
    workbook,
    request,
    storageDataOption,
  }: {
    workbook: WorkbookEntity.SupportedWorkbook
    request?: FilterRequestParams['request']
    storageDataOption: 'localStorage-by-id' | 'sessionStorage-by-id' | 'localStorage-default' | null
  }) => {
    if (this.workbook && this.workbook.id === workbook.id) {
      return
    }

    this.filter = new MakeLessonMaterialFilterService<학습지필터.지원교재필터>({
      request: {
        ...defaultWorksheetSupportedWorkbookFilterParams,
        limitProblemCountPerConcept: null,
        filterId: null,
        workbookId: workbook.id,
        workbookProblemIdList: [],
        exampleProblemIds: [],
        pairX: workbook.pairFlag
          ? workbook.pairX
          : defaultWorksheetSupportedWorkbookFilterParams.pairX,
        ...request,
      },
      requestAsync: worksheetController.postFilterBySupportedWorkbook,
    })

    this.workbook = WorkbookEntity.createWorkbook<typeof workbook.type>(workbook)

    if (workbook?.pairFlag !== undefined && workbook.pairFlag === false) {
      this.filter.request.pairX = null
    }
    try {
      globalLoaderService.openLoader()

      await this.fetchWorkbookPageList(workbook.id)

      if (storageDataOption !== null) {
        await this.initFilterByStorageData({
          workbook: this.workbook,
          filter: this.filter,
          storageDataOption,
        })
      }

      await this.onUpdateFilter()
    } catch (e) {
      toastError(TOAST_STR.errorMessage)
    } finally {
      globalLoaderService.closeLoader()
    }
  }

  selectPage = async (selectedPage: WorkbookPage | null) => {
    if (!this.workbookPageColl) return

    if (selectedPage === null) {
      this.selectedPageId = undefined
      return
    }

    runInAction(() => {
      this.selectedPageId = selectedPage.id
    })
  }

  onUpdateFilter = async (data?: Partial<학습지필터.지원교재필터>) => {
    try {
      this.isLoading = true

      const workbookProblemIdList: number[] = toJS(this.workbookPageColl?.allCheckedList) || []

      if (!this.workbook || !this.filter || workbookProblemIdList.length === 0) return

      const params = {
        ...this.filter?.request,
        ...data,
        workbookProblemIdList,
        workbookId: this.workbook.id,
      }

      if (this.showExampleProblemImage) {
        params.exampleProblemIds = this.workbookPageColl?.allCheckedExampleProblemList || []
      }

      await this.filter.updateAsync(params)
      this.isLoading = false
    } catch (e) {
      if (e.message === 'canceled') return
      this.isLoading = false
    }
  }

  fetchWorkbookPageList = async (workbookId: WorkbookEntity.Workbook['id']) => {
    const workbookPageColl =
      await workbookController.supportedWorkbook.fetchWorkbookProblemPageList(workbookId)
    this.workbookPageColl = workbookPageColl
    return this.workbookPageColl
  }

  saveShowExampleImageOption = ({
    시중교재_예시문항보기_여부,
  }: {
    시중교재_예시문항보기_여부: boolean
  }) => {
    localStorageService.setItem('시중교재_예시문항보기_여부')(시중교재_예시문항보기_여부)
  }

  saveDraftFilter = () => {
    if (!this.workbook || !this.filter) return
    const filter: ValueOf<LocalStorageDataT['LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK_ID']> = {
      originSupportedWorkbookId: this.workbook.id,
      originSupportedWorkbookProblemIds: this.workbookPageColl?.allCheckedList || [],
      pairX: this.filter.request.pairX,
      similarX: this.filter.request.similarX,
      similarLevel: this.filter.request.similarLevel,
      practiceTestOption: this.filter.request.practiceTest,
      answerTypes:
        this.filter.request.problemFilterType === 'ALL'
          ? ANSWER_TYPE.전체
          : this.filter.request.problemFilterType === 'CHOICE'
            ? ANSWER_TYPE.객관식
            : ANSWER_TYPE.주관식,
      autoScorableOnly: this.filter.request.onlyAutoScorable,
      excludePrevious: this.filter.request.excludePrevious,
      // 제외 기준만 영속화하고 대상 학생(studentIds)은 저장하지 않는다. 제외 비활성 시 기준도 비운다.
      previousExclusionScope: this.filter.request.excludePrevious
        ? this.filter.request.previousExclusionScope
        : undefined,
      excludeOutOfCurriculum: this.filter.request.excludeOOC,
      limitProblemCountPerConcept: this.filter.request.limitProblemCountPerConcept,
      includeSameProblem: this.filter.request.includeSameProblem,
      groupingChapterType: 'LITTLE_CHAPTER',
      steps: null,
      level: null,
    }

    SupportedWorkbookDetailService.storage.saveDraft(this.workbook.id, filter)
    localStorageService.setItem('LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK')(filter)
  }

  initFilterByStorageData = async ({
    workbook,
    filter,
    storageDataOption,
  }: {
    workbook: WorkbookEntity.SupportedWorkbook
    filter: MakeLessonMaterialFilterService<학습지필터.지원교재필터>
    storageDataOption: 'localStorage-by-id' | 'sessionStorage-by-id' | 'localStorage-default'
  }) => {
    // const showExampleProblemImage = localStorageService.getItem('시중교재_예시문항보기_여부')
    // this.showExampleProblemImage = !!showExampleProblemImage

    const sessionStorageDataById = SupportedWorkbookDetailService.storage.popDraft(workbook.id)
    const localStorageDataById = SupportedWorkbookDetailService.storage.get(workbook.id)

    const storageDataById =
      storageDataOption === 'localStorage-by-id'
        ? localStorageDataById
        : storageDataOption === 'sessionStorage-by-id'
          ? sessionStorageDataById
          : null

    // id로 조회한 결과가 있다면,
    if (storageDataById) {
      if (!this.workbookPageColl) {
        await this.fetchWorkbookPageList(workbook.id)
      }

      const { selectedPageSize, workbookPageColl, minWorkbookPage } =
        SupportedWorkbookDetailService.getWorkbookPageInfoByPresetProblemIds(
          this.workbookPageColl!,
          storageDataById.originSupportedWorkbookProblemIds,
        )

      if (storageDataOption === 'localStorage-by-id') {
        // globalLoaderService.closeLoader()
        // await new Promise<void>((resolve) => {
        //   modalService.openModal(
        //     <SupportedWorkbookSameOptionModal
        //       totalPageSize={workbookPageColl.size}
        //       selectedPageSize={selectedPageSize}
        //       workbook={workbook}
        //       storageData={storageDataById}
        //       onSubmit={async (allPageOn) => {
        //         if (allPageOn) {
        //           workbookPageColl.allCheck()
        //         }
        //         this.workbookPageColl = workbookPageColl
        //         this.selectPage(minWorkbookPage)
        //         resolve()
        //         modalService.closeModal()
        //       }}
        //     />,
        //     {
        //       onClose: () => {
        //         resolve()
        //         return true
        //       },
        //     },
        //   )
        // })
      } else {
        this.workbookPageColl = workbookPageColl
        this.selectPage(minWorkbookPage)
      }
      this.updateFilterByStorageData(workbook, filter, storageDataById)
    } else {
      // id로 조회한 결과가 없다면 기본 저장소에서 마지막으로 저장된 필터값을 사용
      const defaultStorageData = localStorageService.getItem(
        'LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK',
      )

      if (defaultStorageData) {
        this.updateFilterByStorageData(workbook, filter, defaultStorageData)
      }
    }

    globalLoaderService.closeLoader()
  }

  updateFilterByStorageData = (
    workbook: WorkbookEntity.SupportedWorkbook,
    filter: MakeLessonMaterialFilterService<학습지필터.지원교재필터>,
    storageData: LocalStorageDataT['LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK'],
  ) => {
    if (!workbook.pairFlag || !workbook.pairX) {
      filter.request.pairX = null
    } else {
      if ('pairX' in storageData && storageData.pairX !== null) {
        filter.request.pairX =
          storageData.pairX > workbook.pairX ? workbook.pairX : storageData.pairX
      }
    }
    filter.request.similarX = storageData.similarX
    filter.request.similarLevel = storageData.similarLevel
    filter.request.problemFilterType =
      storageData.answerTypes.length === 4
        ? 'ALL'
        : storageData.answerTypes.includes('SINGLE_CHOICE')
          ? 'CHOICE'
          : 'SHORT_ANSWER'
    filter.request.onlyAutoScorable = storageData.autoScorableOnly

    if (localStorageService.getItem('시중교재_예시문항보기_여부')) {
      this.showExampleProblemImage = true
    }
    filter.request.practiceTest = storageData.practiceTestOption
    // 체크박스 비노출 variant에서 저장된 true가 복원되면 사용자가 해제할 방법이 없다
    filter.request.excludePrevious = getMakeVariantCapabilities(window.location.search)
      .showExcludePrevious
      ? storageData.excludePrevious
      : false
    // 제외 기준은 복원(제외 비활성이면 비움), 대상 학생은 복원하지 않음(재진입 시 다시 선택)
    filter.request.previousExclusionScope = filter.request.excludePrevious
      ? storageData.previousExclusionScope
      : undefined
    filter.request.excludeOOC = storageData.excludeOutOfCurriculum

    if (storageData.limitProblemCountPerConcept !== undefined) {
      filter.request.limitProblemCountPerConcept = storageData.limitProblemCountPerConcept
    }

    if ('includeSameProblem' in storageData) {
      filter.request.includeSameProblem = storageData.includeSameProblem
    }
  }
}

function _saveToStorage(storageService: StorageService) {
  return (
    workbookId: WorkbookEntity.SupportedWorkbook['id'],
    data: ValueOf<LocalStorageDataT['LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK_ID']>,
  ) => {
    const storageMap = _getStorageMap(storageService)

    storageMap.set(String(workbookId), data)

    _setStorageMap(storageService)(storageMap)
  }
}

function _getStorageMap(storageService: StorageService) {
  const storageData = storageService.getItem('LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK_ID')
  return storageData
    ? new Map<string, ValueOf<LocalStorageDataT['LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK_ID']>>(
        Object.entries(storageData),
      )
    : new Map<string, ValueOf<LocalStorageDataT['LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK_ID']>>()
}

function _getItem(storageService: StorageService) {
  return (workbookId: WorkbookEntity.SupportedWorkbook['id']) => {
    const storageMap = _getStorageMap(storageService)
    return storageMap.get(String(workbookId))
  }
}

function _setStorageMap(storageService: StorageService) {
  return (
    map: Map<string, ValueOf<LocalStorageDataT['LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK_ID']>>,
  ) => {
    if (map.size === 0) {
      storageService.removeItem('LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK_ID')
    } else {
      storageService.setItem('LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK_ID')(
        Object.fromEntries(map),
      )
    }
  }
}
